import { useState, useRef } from 'react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

// Choose which part of the portal header photo stays in view. Click or drag on
// the full photo to set the focal point; the previews show the header as a
// wide desktop banner and a tall phone header, under the same dark overlay.
const DEFAULT = { x: 50, y: 40 };
const clamp = (n) => Math.max(0, Math.min(100, Math.round(n)));

function Preview({ url, focus, width, height, label }) {
  return (
    <div>
      <div style={{ width, height, position: 'relative', overflow: 'hidden', borderRadius: 'var(--radius-md)', background: '#11171E' }}>
        <img src={url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${focus.x}% ${focus.y}%` }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(17,23,30,0.86) 0%, rgba(17,23,30,0.84) 50%, rgba(17,23,30,0.95) 100%)' }} />
        <div style={{ position: 'absolute', left: 12, bottom: 10, color: '#fff', fontSize: 13, fontWeight: 600 }}>Project title</div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

export default function HeaderFocus({ url, value, onSave, onCancel }) {
  const [focus, setFocus] = useState(value && Number.isFinite(value.x) ? value : DEFAULT);
  const [dragging, setDragging] = useState(false);
  const box = useRef(null);
  const pointAt = (e) => {
    const r = box.current.getBoundingClientRect();
    setFocus({ x: clamp(((e.clientX - r.left) / r.width) * 100), y: clamp(((e.clientY - r.top) / r.height) * 100) });
  };
  const nudge = (e) => {
    const step = e.shiftKey ? 10 : 2;
    const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (!d) return;
    e.preventDefault();
    setFocus((f) => ({ x: clamp(f.x + d[0]), y: clamp(f.y + d[1]) }));
  };
  return (
    <Card>
      <div style={{ fontSize: 14 }}>Portal header image position</div>
      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', margin: '2px 0 12px' }}>
        Click or drag on the photo to choose the point that stays in view. Arrow keys nudge it (Shift for bigger steps).
      </div>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div ref={box} role="slider" tabIndex={0} aria-label="Focal point" aria-valuemin={0} aria-valuemax={100} aria-valuenow={focus.x} aria-valuetext={`${focus.x}% across, ${focus.y}% down`}
          onKeyDown={nudge}
          onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); setDragging(true); pointAt(e); }}
          onPointerMove={(e) => dragging && pointAt(e)}
          onPointerUp={() => setDragging(false)}
          style={{ position: 'relative', width: 360, maxWidth: '100%', cursor: 'crosshair', userSelect: 'none', touchAction: 'none', lineHeight: 0 }}>
          <img src={url} alt="" draggable={false} style={{ width: '100%', display: 'block', borderRadius: 'var(--radius-md)' }} />
          <span aria-hidden="true" style={{ position: 'absolute', left: `${focus.x}%`, top: `${focus.y}%`, width: 22, height: 22, marginLeft: -11, marginTop: -11, borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 0 0 2px #FF6600' }} />
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Preview url={url} focus={focus} width={300} height={70} label="Desktop header" />
          <Preview url={url} focus={focus} width={90} height={150} label="Phone header" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <Button size="sm" onClick={() => onSave(focus)}>Save position</Button>
        <Button size="sm" variant="secondary" onClick={() => setFocus(DEFAULT)}>Reset to centre</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </Card>
  );
}
